/**
 * Settings Page
 * Organization settings and configuration
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SubscriptionLayout from '../../components/subscription/SubscriptionLayout';
import { useAuth } from '../../contexts/AuthContext';
import AzureADConfigSection from '../../components/subscription/AzureADConfigSection';
import PasswordSettingsSection from '../../components/subscription/PasswordSettingsSection';
import { Settings as SettingsIcon } from 'lucide-react';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { getSubscription } from '../../services/SubscriptionService';

const SettingsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isIndividualSubscription, setIsIndividualSubscription] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      const orgId = searchParams.get('org') || user?.organizationId || null;
      console.log('[SettingsPage] organizationId:', orgId, 'user:', user);

      // For individual subscriptions, check subscription using user.id
      const subscriptionOrgId = orgId || (user?.id || null);

      if (!subscriptionOrgId) {
        // Wait a bit for user to load, then redirect if still no orgId
        const timer = setTimeout(() => {
          navigate('/admin/login');
        }, 1000);
        setIsLoading(false);
        return () => clearTimeout(timer);
      }

      // Check if this is an individual subscription
      try {
        const subscription = await getSubscription(subscriptionOrgId);
        if (subscription?.planType === 'individual') {
          setIsIndividualSubscription(true);
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error('[SettingsPage] Error checking subscription:', error);
      }

      // Only allow organization settings if there's a valid organization ID
      // (not for individual subscriptions)
      if (!orgId || orgId === user?.id) {
        setIsIndividualSubscription(true);
        setIsLoading(false);
        return;
      }

      setOrganizationId(orgId);
      setIsLoading(false);
    };

    loadSettings();
  }, [searchParams, user, navigate]);

  if (isLoading) {
    return (
      <SubscriptionLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </SubscriptionLayout>
    );
  }

  // If individual subscription, show message that organization settings are not available
  if (isIndividualSubscription) {
    return (
      <SubscriptionLayout>
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent flex items-center space-x-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25">
                <SettingsIcon className="w-7 h-7 text-white" />
              </div>
              <span>Organization Settings</span>
            </h1>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
              Organization Settings Not Available
            </h3>
            <p className="text-blue-800 dark:text-blue-200 mb-2">
              Organization settings are only available for organization subscriptions (Starter, Professional, or Enterprise plans).
            </p>
            <p className="text-blue-600 dark:text-blue-300 text-sm">
              Your current plan: Individual. To access organization settings, please upgrade to an organization subscription.
            </p>
          </div>
        </div>
      </SubscriptionLayout>
    );
  }

  if (!organizationId) {
    return (
      <SubscriptionLayout>
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent flex items-center space-x-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25">
                <SettingsIcon className="w-7 h-7 text-white" />
              </div>
              <span>Organization Settings</span>
            </h1>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-xl p-4">
            <p className="text-yellow-800 dark:text-yellow-200">
              No organization found. Please ensure you are associated with an organization.
            </p>
          </div>
        </div>
      </SubscriptionLayout>
    );
  }

  // Check if user has admin access (only org admins can configure Azure AD)
  const isOrgOwner = user && (user.role === 'super_admin' || user.role === 'org_admin');
  console.log('[SettingsPage] isOrgOwner:', isOrgOwner, 'user.role:', user?.role);

  return (
    <SubscriptionLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25">
              <SettingsIcon className="w-7 h-7 text-white" />
            </div>
            <span>Organization Settings</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2 font-medium">
            Manage your organization settings and integrations
          </p>
        </div>

        {/* Azure AD Configuration - Only for org owners */}
        {isOrgOwner ? (
          <AzureADConfigSection organizationId={organizationId} />
        ) : (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4">
            <p className="text-blue-800 dark:text-blue-200">
              Organization settings are only available to organization administrators.
              Please contact your organization administrator to configure Azure AD settings.
            </p>
            <p className="text-blue-600 dark:text-blue-300 text-sm mt-2">
              Your role: {user?.role || 'Unknown'}
            </p>
          </div>
        )}

        {/* Password Settings - Available for all authenticated users */}
        <PasswordSettingsSection />
      </div>
    </SubscriptionLayout>
  );
};

export default SettingsPage;

